import type { Config } from '@backstage/config';
import { endpointsForHost, type GitHubEndpoints } from './endpoints';

/**
 * Everything a caller needs to talk to, or link into, the GitHub instance the
 * portal is wired to. Resolved from config so that no call site hardcodes a
 * hostname or an organization name.
 */
export type GitHubContext = GitHubEndpoints & {
  host: string;
  organization: string;
  /** Org that owns application repositories created through the portal, and the default for new ones. */
  applicationOrganization: string;
  /** Every org applications may be created in, `applicationOrganization` first. */
  applicationOrganizations: string[];
  complianceRepository: string;
  identityVendingMachineRepository: string;
  /** `owner/name` of repositories every employee may read without connecting GitHub. */
  sharedRepositories: string[];
  /** Document folders by lower-cased `owner/name`, for repositories that don't use `docs/`. */
  repositoryDocsPaths: Record<string, string[]>;
};

/** Where a repository's documents live when config doesn't say otherwise. */
export const DEFAULT_DOCS_PATHS: readonly string[] = ['docs'];

const DEFAULT_COMPLIANCE_REPO_NAME = 'vivicta-compliance-as-code';
const DEFAULT_IDENTITY_VENDING_MACHINE_REPO_NAME =
  'vivicta-identity-vending-machine';

/**
 * Prefer the explicit `apiBaseUrl`/`rawBaseUrl` on the matching
 * `integrations.github` entry over deriving them from the hostname.
 */
function endpointsFromIntegrations(
  config: Config,
  host: string,
): GitHubEndpoints {
  const derived = endpointsForHost(host);
  const integration = config
    .getOptionalConfigArray('integrations.github')
    ?.find(entry => entry.getOptionalString('host') === host);

  return {
    webBaseUrl: derived.webBaseUrl,
    apiBaseUrl:
      integration?.getOptionalString('apiBaseUrl') ?? derived.apiBaseUrl,
    rawBaseUrl:
      integration?.getOptionalString('rawBaseUrl') ?? derived.rawBaseUrl,
  };
}

/** Organizations in order, without repeats; GitHub org names are case-insensitive. */
const uniqueOrganizations = (organizations: string[]) => {
  const seen = new Set<string>();
  return organizations.filter(org => {
    const key = org.toLowerCase();
    if (!org || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export function resolveGitHubContext(config: Config): GitHubContext {
  const host = config.getString('vivicta.github.host');
  const organization = config.getString('vivicta.github.organization');
  const configuredComplianceRepo = config.getOptionalString(
    'vivicta.github.complianceRepository',
  );

  const applicationOrganization =
    config.getOptionalString('vivicta.github.applicationOrganization') ??
    organization;

  return {
    host,
    organization,
    applicationOrganization,
    applicationOrganizations: uniqueOrganizations([
      applicationOrganization,
      ...(config.getOptionalStringArray(
        'vivicta.github.applicationOrganizations',
      ) ?? []),
    ]),
    complianceRepository: qualifyRepo(
      configuredComplianceRepo ?? DEFAULT_COMPLIANCE_REPO_NAME,
      organization,
    ),
    identityVendingMachineRepository: qualifyRepo(
      config.getOptionalString(
        'vivicta.github.identityVendingMachineRepository',
      ) ?? DEFAULT_IDENTITY_VENDING_MACHINE_REPO_NAME,
      organization,
    ),
    sharedRepositories: (
      config.getOptionalStringArray('vivicta.github.sharedRepositories') ?? []
    ).map(repo => qualifyRepo(repo, organization)),
    repositoryDocsPaths: resolveRepositoryDocsPaths(config, organization),
    ...endpointsFromIntegrations(config, host),
  };
}

function resolveRepositoryDocsPaths(
  config: Config,
  organization: string,
): Record<string, string[]> {
  const entries =
    config.getOptionalConfigArray('vivicta.github.repositoryDocs') ?? [];
  return Object.fromEntries(
    entries.map(entry => [
      qualifyRepo(entry.getString('repository'), organization).toLowerCase(),
      entry.getStringArray('paths').map(path => path.replace(/^\/+|\/+$/g, '')),
    ]),
  );
}

/** Folders that hold a repository's documents: configured ones, else `docs`. */
export function docsPathsFor(
  context: Pick<GitHubContext, 'repositoryDocsPaths'>,
  repoFullName: string,
): readonly string[] {
  return (
    context.repositoryDocsPaths[repoFullName.toLowerCase()] ??
    DEFAULT_DOCS_PATHS
  );
}

/** Expands a bare repository name into `owner/name`; passes `owner/name` through. */
export function qualifyRepo(repo: string, organization: string): string {
  return repo.includes('/') ? repo : `${organization}/${repo}`;
}

/** Web URL for a repository, e.g. `https://vivicta.ghe.com/org/repo`. */
export function repoUrl(context: GitHubContext, repoFullName: string): string {
  return `${context.webBaseUrl}/${repoFullName}`;
}

/** Web URL for a file in a repository, at a branch, tag or commit. */
export function repoFileUrl(
  context: GitHubContext,
  repoFullName: string,
  filePath: string,
  ref = 'main',
): string {
  return `${repoUrl(context, repoFullName)}/blob/${ref}/${filePath.replace(
    /^\//,
    '',
  )}`;
}
